

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignsService } from '../../../../core/services/campaigns/campaigns.service';
import { OrdersService } from '../../../../core/services/orders/orders.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';
import { FormatNumberPipe, FormatNumberPipe2, FormatNumberPipe3 } from '../../../../core/pipe/custom-pipes';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-edit-config-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormatNumberPipe, FormatNumberPipe2, FormatNumberPipe3],
  templateUrl: './edit-config-dialog.component.html',
  styleUrl: './edit-config-dialog.component.css'
})
export class EditConfigDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() orderData: any;
  @Output() close = new EventEmitter<void>();
  @Output() updated = new EventEmitter<any>();

  loading = true;
  submitting = false;
  matrixData: { matrix_base: any; matrix_total: any; matrix_tax: string } | null = null;
  columns: string[] = [];
  rows: string[] = [];
  matrixDisplay: any = {};
  showIva = true;

  selectedCol = '';
  selectedRow = '';

  get taxRate(): number {
    return this.authStateService.getSupplierTax() || 0.21;
  }

  get canSubmit(): boolean {
    if (this.submitting || !this.selectedCol || !this.selectedRow) return false;
    // Don't allow submitting if the same config is selected
    const currentDuration = String(this.orderData?.duration);
    const currentKms = String(this.orderData?.yearly_kms);
    return !(this.selectedCol === currentDuration && this.selectedRow === currentKms);
  }

  get selectedBase(): number | null {
    if (!this.selectedCol || !this.selectedRow || !this.matrixData?.matrix_base) return null;
    return this.matrixData.matrix_base[this.selectedCol]?.[this.selectedRow] ?? null;
  }

  get selectedTotal(): number | null {
    if (!this.selectedCol || !this.selectedRow || !this.matrixData?.matrix_total) return null;
    return this.matrixData.matrix_total[this.selectedCol]?.[this.selectedRow] ?? null;
  }

  constructor(
    private campaignsService: CampaignsService,
    private ordersService: OrdersService,
    private alertService: AlertService,
    private authStateService: AuthStateService
  ) {}

  ngOnInit(): void {
    this.loadMatrix();
  }

  loadMatrix(): void {
      
    const campaignRef = this.orderData?.campaign_id;
    if (!campaignRef) {
      this.loading = false;
      return;
    }

    this.campaignsService.getMatrix(campaignRef)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.matrixData = res.data;
          this.updateMatrixDisplay();
          this.loading = false;

          // Pre-select current config
          const currentDuration = String(this.orderData?.duration);
          const currentKms = String(this.orderData?.yearly_kms);
          if (this.matrixDisplay[currentDuration]?.[currentKms] != null) {
            this.selectedCol = currentDuration;
            this.selectedRow = currentKms;
          }
        },
        error: () => {
          this.loading = false;
          this.alertService.error('swal_messages.general.error');
        }
      });
  }

  updateMatrixDisplay(): void {
    if (!this.matrixData) return;
    const matrix = this.showIva ? this.matrixData.matrix_total : this.matrixData.matrix_base;
    if (!matrix) {
      this.columns = [];
      this.rows = [];
      this.matrixDisplay = {};
      return;
    }
    this.matrixDisplay = matrix;
    this.columns = Object.keys(matrix).sort((a, b) => +a - +b);
    const kmSet = new Set<string>();
    Object.values(matrix).forEach(row => {
      Object.keys(row as object).forEach(km => kmSet.add(km));
    });
    this.rows = Array.from(kmSet).sort((a, b) => +a - +b);
  }

  onIvaToggle(event: Event): void {
    this.showIva = (event.target as HTMLInputElement).checked;
    this.updateMatrixDisplay();
  }

  selectCell(col: string, row: string): void {
    if (this.matrixDisplay[col]?.[row] == null) return;
    this.selectedCol = col;
    this.selectedRow = row;
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (!this.canSubmit || this.submitting) return;

    const matrixBase = this.matrixData?.matrix_base;
    const matrixTotal = this.matrixData?.matrix_total;
    const baseRaw = matrixBase?.[this.selectedCol]?.[this.selectedRow];
    const totalRaw = matrixTotal?.[this.selectedCol]?.[this.selectedRow];

    let base: number, tax: number, total: number;
    if (baseRaw != null && totalRaw != null) {
      base = +baseRaw;
      total = +totalRaw;
      tax = +(total - base).toFixed(2);
    } else if (baseRaw != null) {
      base = +baseRaw;
      tax = +(base * this.taxRate).toFixed(2);
      total = +(base + tax).toFixed(2);
    } else if (totalRaw != null) {
      total = +totalRaw;
      base = +(total / (1 + this.taxRate)).toFixed(2);
      tax = +(total - base).toFixed(2);
    } else {
      return;
    }

    const duration = +this.selectedCol;
    const yearlyKms = +this.selectedRow;
    const totalKms = Math.round(yearlyKms * (duration / 12));

    const body = {
      duration: duration,
      yearly_kms: yearlyKms,
      total_kms: totalKms,
      monthly_fee_base: +(base.toFixed(2)),
      monthly_fee_tax: +(tax.toFixed(2)),
      monthly_fee_total: +(total.toFixed(2)),
    };

    this.submitting = true;
    this.alertService.loading('swal_messages.updating_order');

    this.ordersService.updateData(this.orderData.reference, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.submitting = false;
          this.alertService.successOk('swal_messages.order_updated')
            .then((result) => {
              if (result.isConfirmed) {
                this.updated.emit(data.data);
              }
            });
        },
        error: (error: any) => {
          this.submitting = false;
          const errors = error?.error?.errors;
          if (errors) {
            const firstKey = Object.keys(errors)[0];
            const msg = errors[firstKey]?.[0]?.msg || 'swal_messages.general.error';
            this.alertService.error(msg);
          } else {
            this.alertService.error('swal_messages.general.error');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
